import React, { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormField from "../../components/form-field";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../service/supabase";
import toast from "react-hot-toast";
import { FiLock, FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router";

const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);

  const passwordValidationSchema = Yup.object({
    currentPassword: Yup.string()
      .min(6, "Passowrdi duhet te jete me i gjatë se 6 karaktere")
      .required("Passowrdi eshte i domosdshem"),
    newPassword: Yup.string()
      .min(6, "Passowrdi duhet te jete me i gjatë se 6 karaktere")
      .required("Passowrdi eshte i domosdshem"),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref("newPassword"), null],
        "Passowrdit duhet te jete i njejte"
      )
      .required("Please confirm your new password"),
  });

  useEffect(() => {
    let mounted = true;

    const fetchProfile = async () => {
      if (!user?.id || !mounted) return;

      try {
        const { error } = await supabase
          .from("profiles")
          .select("first_name, last_name")
          .eq("id", user.id)
          .single();

        if (!mounted) return;

        if (error && error.code !== "PGRST116") {
          console.error("Error fetching profile:", error);
          setIsLoadingInitial(false);
          return;
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        if (mounted) {
          setIsLoadingInitial(false);
        }
      }
    };

    fetchProfile();

    return () => {
      mounted = false;
    };
  }, [user?.id, user?.email]);

  const handlePasswordReset = async (values, { resetForm }) => {
    setIsLoadingPassword(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: values.currentPassword,
      });
      if (signInError) {
        toast.error("Passowrdi aktual eshte i pasaktë");
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: values.newPassword,
      });

      if (updateError) throw updateError;

      toast.success("Passowrdi u ndryshua me sukses!");
      resetForm();
    } catch (error) {
      console.error("Error gjate ndryshimit te passowrdit:", error);
      toast.error(
        "Nuk mund te ndryshohet passowrdi. Ju lutem provoni perseri."
      );
    } finally {
      setIsLoadingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <FiArrowLeft className="mr-2" />
            Mbrapa
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            Caktimet e profilit
          </h1>
          <p className="text-gray-600 mt-2">
            Administroni informacionin e profilit dhe caktimet e sigurisë
          </p>
        </div>

        {isLoadingInitial ? (
          <div className="space-y-6">
            <div className="bg-white shadow-md rounded-lg p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                    <div>
                      <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                  <div>
                    <div className="h-3 bg-gray-200 rounded w-24 mb-2"></div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-white shadow-md rounded-lg p-6">
              <div className="flex items-center mb-6">
                <FiLock className="text-2xl text-blue-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Ndrysho passowrdin
                </h2>
              </div>

              <Formik
                initialValues={{
                  currentPassword: "",
                  newPassword: "",
                  confirmPassword: "",
                }}
                validationSchema={passwordValidationSchema}
                onSubmit={handlePasswordReset}
              >
                {({ isValid, dirty }) => (
                  <Form className="space-y-4">
                    <FormField
                      htmlFor="currentPassword"
                      label="Passowrdi aktual"
                      id="currentPassword"
                      name="currentPassword"
                      placeholder="Jepni passowrdin aktual"
                      type="password"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        htmlFor="newPassword"
                        label="Passowrdi i ri"
                        id="newPassword"
                        name="newPassword"
                        placeholder="Jepni passowrdin e ri"
                        type="password"
                      />{" "}
                      <FormField
                        htmlFor="confirmPassword"
                        label="Konfirmo passowrdin e ri"
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder="Konfirmoni passowrdin e ri"
                        type="password"
                      />
                    </div>

                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={!isValid || !dirty || isLoadingPassword}
                        className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2
                      ${
                        !isValid || !dirty || isLoadingPassword
                          ? "bg-blue-300 text-white cursor-not-allowed"
                          : "bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
                      }`}
                      >
                        {isLoadingPassword ? (
                          <>
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            Po ndryshohet...
                          </>
                        ) : (
                          "Ndrysho passowrdin"
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
